import { observer } from 'mobx-react';
import { Spinner } from 'office-ui-fabric-react/lib/Spinner';
import {
    FileProviderType,
    FileProviderViewState,
    lazyAddFileProvider,
    lazyLoadProviders,
    lazyRemoveFileProvider,
    ProviderLoadStateOption,
} from 'owa-fileprovider-store';
import { getAddableFileProviders } from 'owa-fileprovider-store/lib/selectors/getAddableFileProviders';
import { getFileProviders } from 'owa-fileprovider-store/lib/selectors/getFileProviders';
import { getProviderLoadState } from 'owa-fileprovider-store/lib/selectors/getProviderLoadState';
import { isProviderBeingRemoved } from 'owa-fileprovider-store/lib/selectors/isProviderBeingRemoved';
import getProviderBrandName from 'owa-fileprovider-store/lib/utils/getProviderBrandName';
import { LightningId } from 'owa-lightning-v2';
import loc from 'owa-localize';
import AttachmentDataProviderType from 'owa-service/lib/contract/AttachmentDataProviderType';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { AddStorageAccountCallout } from './AddStorageAccountCallout';
import {
    storageAccounts_addAccountHeader,
    storageAccounts_otherAccounts,
} from './FileProviderAccountsView.locstring.json';
import FileProvidersToAddListView, { FileProviderToAddInfo } from './FileProvidersToAddListView';
import HeaderContainer from './HeaderContainer';
import StorageAccountsListView from './StorageAccountsListView';

export interface AddProviderState {
    isOpen: boolean;
}

const styles = require('./FileProviderAccountsView.scss');

@observer
export default class FileProviderAccountsView extends React.Component<
    React.HTMLProps<HTMLDivElement>,
    AddProviderState
> {
    private addFileProviderAction: (providerType: FileProviderType) => void = null;
    private calloutTarget: HTMLElement;

    constructor(props: {}) {
        super(props);
        this.state = {
            isOpen: true,
        };
    }
    componentDidMount() {
        // Initialize the addFileProvider to avoid popup blocker to trigger
        lazyAddFileProvider.import().then(addFileProvider => {
            this.addFileProviderAction = addFileProvider;
        });
        this.initializeStorageAccountsOption();
    }

    render() {
        const addedAccounts = getFileProviders();
        const additionalAccounts = this.getAdditionalStorageAccounts(addedAccounts);
        const providersToAddInfo = this.getStorageAccountToAdd(getAddableFileProviders());

        return getProviderLoadState() === ProviderLoadStateOption.Loaded ? (
            <div className={styles.container}>
                <div>
                    {providersToAddInfo.length !== 0 && (
                        <div>
                            <HeaderContainer
                                className={styles.fileProvidersToAddContainer}
                                ref={this.setTargetRef}
                                header={loc(storageAccounts_addAccountHeader)}>
                                <FileProvidersToAddListView
                                    providersToAddInfo={providersToAddInfo}
                                />
                            </HeaderContainer>
                            <AddStorageAccountCallout
                                lid={LightningId.AddStorageAccountCallout} // lightning id for callout
                                when={this.activateCallout}
                                target={this.setTarget}
                            />
                        </div>
                    )}

                    {additionalAccounts && additionalAccounts.length !== 0 && (
                        <StorageAccountsListView
                            header={loc(storageAccounts_otherAccounts)}
                            storageAccounts={additionalAccounts}
                            onRemoveHandler={this.removeAccount}
                        />
                    )}
                </div>
            </div>
        ) : (
            <Spinner />
        );
    }

    private setTargetRef = (ref: HTMLElement) => {
        return (this.calloutTarget = ReactDOM.findDOMNode(ref) as HTMLElement);
    };

    private setTarget = () => {
        return this.calloutTarget;
    };

    private activateCallout(lightup) {
        setTimeout(lightup, 500);
    }

    private getStorageAccountToAdd(accountsToAdd: FileProviderType[]): FileProviderToAddInfo[] {
        return accountsToAdd.map(account => {
            return {
                providerType: account,
                providerName: getProviderBrandName(account),
                onClick: this.getAddStorageAccountFunc(account),
            };
        });
    }

    private async initializeStorageAccountsOption() {
        (await lazyLoadProviders.import())();
    }

    private getAddStorageAccountFunc = (providerType: FileProviderType) => {
        return () => {
            if (this.addFileProviderAction) {
                this.addFileProviderAction(providerType);
            } else {
                lazyAddFileProvider.import().then(addFileProviderAction => {
                    addFileProviderAction(providerType);
                });
            }
        };
    };

    private getAdditionalStorageAccounts(
        providers: FileProviderViewState[]
    ): FileProviderViewState[] {
        return providers.filter(
            p =>
                !p.isPrimaryProvider &&
                !p.associatedWithPrimaryProvider &&
                p.type !== AttachmentDataProviderType.Mailbox &&
                p.type !== AttachmentDataProviderType.MailMessage
        );
    }

    private removeAccount(providerType: FileProviderType) {
        if (!isProviderBeingRemoved(providerType)) {
            lazyRemoveFileProvider.import().then(removeFileProviderAction => {
                removeFileProviderAction(providerType);
            });
        }
    }
}
